import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ProductDraft, StoreType } from '../../../types/product'
import { productApis } from '../../../gateways/breadstack/productApis'

export const productDetailQueryKey = (storeId: string, productId: string) =>
  ['product', storeId, productId] as const

export const useProductDetail = (storeId: string, storeType: StoreType, productId: string) =>
  useQuery({
    queryKey: productDetailQueryKey(storeId, productId),
    queryFn: () => productApis.getDetail(storeId, storeType, productId),
    enabled: Boolean(storeId) && Boolean(productId),
    // While a POS resync is running on the backend (product.waitingSyncingFromPos), keep
    // refetching the detail endpoint so the "Syncing data from POS…" status in PosDataCard
    // reflects real completion instead of just the sync-trigger request round-trip.
    refetchInterval: (query) => (query.state.data?.waitingSyncingFromPos ? 3000 : false),
  })

export const useUpdateProduct = (storeId: string, storeType: StoreType) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: Partial<ProductDraft> }) =>
      productApis.update(storeId, storeType, productId, data),
    onSuccess: (_result, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productDetailQueryKey(storeId, productId) })
      queryClient.invalidateQueries({ queryKey: ['products', storeId] })
      queryClient.invalidateQueries({ queryKey: ['variationGroup', storeId, storeType, productId] })
    },
  })
}

export const useDeleteProduct = (storeId: string, storeType: StoreType) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ productId }: { productId: string }) =>
      productApis.bulkUpdate(storeId, storeType, [productId], 'delete'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', storeId] })
    },
  })
}
